import { Marker, Popup } from "react-leaflet";

import * as gj from "geojson-utils";
import {
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import * as EsriGeoCode from "esri-leaflet-geocoder";

import { useTranslator } from "@eo-locale/react";

import { useMediaQuery, useToast } from "@chakra-ui/react";

import { debounce } from "lodash";

import { setMarkerDetailsWithCoord } from "@/redux/features/place-marker/slice";
import { esriToken } from "../../constants";
import { isAnyMarkerInsidePolygon } from "../../utils/helpers";

import { stripAllHtml } from "@/lib";
import { clearAllErrors } from "@/redux/features/place-form/slice";
import { resetMarker } from "@/redux/features/place-marker/slice";
import { setMapCenterCoordinates } from "@/redux/features/place/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { EsriContext } from "../../context/esri-context";
import { EsriContextType } from "../../context/esri-context-type";

const CustomMarkers = ({ pinDetail, viewMarkerClickHandler }: any) => {
  const L = require("leaflet");
  const markerRef = useRef(null);
  const layers = useAppSelector((state) => state.placeLayers.layers);
  const dispatch = useAppDispatch();
  const isEditMode = useAppSelector(
    (state) => state.placesMarkerSlice.isEditMode
  );
  const markerData = useAppSelector((state) => state.placesMarkerSlice.marker);

  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const [isDesktop] = useMediaQuery("(min-width: 768px)");
  const { lat: mlat, lng: mlng } = markerData;
  const [coord, setCoord] = useState([mlat, mlng]);

  const {
    setActionsVisibility,
    setInteractionsVisibility,
    setNextModalAction,
    openUnsavedModal,
    mapRef
  } = useContext(EsriContext) as EsriContextType;

  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );
  const toast = useToast();
  const { translate } = useTranslator();

  const updatedMarkerPostion = async (lat?: any, lng?: any, marker?: any) => {
    // close if any toast is present
    toast.closeAll();

    let isMarkerInsidePolygon = true;

    if (marker) {
      const isPinInsidePolygon = await isAnyMarkerInsidePolygon(
        lng,
        lat,
        layers,
        gj
      );

      if (!isPinInsidePolygon) {
        isMarkerInsidePolygon = false; // marker is outside the polygon show the toast and render the new page
        toast({
          description: translate("places.pinOutsideBoundary"),
          isClosable: true,
          duration: 2000,
          status: "warning",
          containerStyle: {
            marginBottom: "1 !important"
          }
        });
      } else {
        toast.closeAll();
      }

      const updatedMarkerData = {
        ...markerData,
        lat,
        lng
      };
      try {
        EsriGeoCode.reverseGeocode({
          apikey: esriToken
        })
          .latlng([lat, lng])
          .run(function (error, result) {
            if (error) {
              return;
            }

            dispatch(
              setMarkerDetailsWithCoord({
                ...updatedMarkerData,
                address: result.address.LongLabel,
                isMarkerInsidePolygon
              })
            );
          });
      } catch (error) {
        console.error("error", error);
      }
    }
  };
  const onPinClick = () => {
    if (["edit-pin", "create-pin"].includes(rightPanelView) && isDesktop) {
      setNextModalAction(() => handlePinClick);
      openUnsavedModal();

      return;
    }

    handlePinClick();
  };

  const handleFocus = () => {
    if (mapRef && mapRef.dragging) {
      mapRef.dragging.disable();
    }
  };

  const handleBlur = () => {
    if (mapRef && mapRef.dragging) {
      mapRef.dragging.enable();
    }
  };

  const eventHandlers = useMemo(
    () => ({
      dragend: async () => {
        const marker = markerRef?.current;

        const lat = marker?.getLatLng()?.lat;
        const lng = marker?.getLatLng()?.lng;
        await updatedMarkerPostion(lat, lng, marker);
      },
      click: () => {
        onPinClick();
      },
      focus: handleFocus,
      blur: handleBlur,
      keydown: (e) => {
        if (e.originalEvent.key === "Enter") {
          onPinClick();
        }
      }
    }),
    [markerData, pinDetail, markerRef]
  );

  const handlePinClick = () => {
    if (markerRef.current && isAddressVisible) {
      markerRef.current.openPopup();
    }

    dispatch(
      setMapCenterCoordinates({
        lat: mapRef?.getCenter().lat,
        lng: mapRef?.getCenter().lng,
        zoom: mapRef?.getZoom()
      })
    );
    dispatch(resetMarker());
    dispatch(clearAllErrors());

    if (isMobile) {
      setActionsVisibility(false);
      setInteractionsVisibility(true);
    }
    viewMarkerClickHandler(pinDetail, "custom-marker");
  };

  const fetchIcon = (id, pinIcon, pinColor, viewPinDetails) => {
    const anySelectedMarker = viewPinDetails?.id === id || markerData.id == id;
    const iconSize: any = anySelectedMarker ? 4.125 : 3.125;
    const isHiglighted = anySelectedMarker ? "marker-blinking" : "";
    const pinMargin = anySelectedMarker ? -3 : 12;
    const data: any = {
      id,
      html: `
      <span class="icon-style" >
        <span class="marker-pin marker-icon-marker ${isHiglighted}" style="color: ${pinColor};font-size: ${iconSize}rem; margin-top:${pinMargin}px">
          <span class="marker-icon marker-shadow  marker-icon-${pinIcon}"></span>
        </span>
      </span>`
    };
    return new L.divIcon(data);
  };

  const viewPinDetails = useAppSelector((state) => state.place.pinDetails);

  const { lat, lng, id, comment, pinIcon, pinColor } = pinDetail;

  const isAddressVisible = useAppSelector(
    (state) => state.place.place.showAddress
  );

  const addressDetails = useMemo(
    () => viewPinDetails?.address,
    [viewPinDetails, rightPanelView]
  );

  const enableEditFeature = markerData && markerData?.id === id && isEditMode;

  const isDraggable = () => {
    return enableEditFeature;
  };

  const getLatLng = () => {
    try {
      if (enableEditFeature) {
        const { lat, lng } = markerData;
        return new L.LatLng(lat, lng);
      }

      return new L.LatLng(lat, lng);
    } catch (error) {
      console.error("Error in getLatLng", error);
    }
  };

  const popAddress = useCallback(() => {
    const isEditOrCreatePin = ["edit-pin", "create-pin"].includes(
      rightPanelView
    );

    if (isEditOrCreatePin) return <></>;

    return (
      isAddressVisible && (
        <Popup key={id}>
          {addressDetails ? addressDetails : translate("common.loading")}
        </Popup>
      )
    );
  }, [viewPinDetails, rightPanelView]);

  const debounceUpdate = useCallback(
    debounce((newLat, newLng) => {
      updatedMarkerPostion(newLat, newLng, markerRef?.current);
    }, 1000),
    [markerRef]
  );

  useEffect(() => {
    const handleKeyDown: any = (event: KeyboardEvent) => {
      const marker = markerRef?.current;
      if (!marker) return;

      const lat = marker?.getLatLng()?.lat;
      const lng = marker?.getLatLng()?.lng;

      let newLat = lat;
      let newLng = lng;
      const zoom = mapRef.getZoom();
      const baseStep = 0.01;

      // Adjust step size based on zoom level
      const step = baseStep / Math.pow(2, zoom - 10);
      const combo = event.shiftKey && event.key;

      switch (combo) {
        case "ArrowUp":
          newLat = lat + step;
          setCoord([newLat, lng]);
          break;
        case "ArrowDown":
          newLat = lat - step;
          setCoord([newLat, lng]);
          break;
        case "ArrowLeft":
          newLng = lng - step;
          setCoord([lat, newLng]);
          break;
        case "ArrowRight":
          newLng = lng + step;
          setCoord([lat, newLng]);

          break;
        default:
          break;
      }

      debounceUpdate(newLat, newLng);
    };
    isEditMode &&
      markerData.id === pinDetail.id &&
      document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [markerData, isEditMode, markerRef, coord]);

  if (isEditMode && markerData.id !== pinDetail.id) return null;

  return (
    <Marker
      key={`${id}-${isEditMode}`}
      position={isEditMode ? coord : getLatLng()}
      ref={markerRef}
      draggable={isDraggable()}
      icon={fetchIcon(id, pinIcon, pinColor, viewPinDetails)}
      riseOnHover={true}
      riseOffset={90}
      title={comment ? stripAllHtml(comment) : ""}
      autoPan
      eventHandlers={eventHandlers}
      keyboard={true}
    >
      {popAddress()}
    </Marker>
  );
};

export default CustomMarkers;
